import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearch, useGlobalContext, TripType, setLastSearch, setRecentSearch, useCurrentOpenSearchField, SearchFieldEnum, useQueryParam, } from '@honeycomb/data';
import { FunnelStep, router, searchCreatedRouting } from '@honeycomb/routing';
import { pushSearchCriteria, pushHotelDetails } from '@honeycomb/tracking';
import { Container, useBreakpoints, useDisclosure } from '@honeycomb/ui-core';
import { useRouter } from 'next/router';
import { Search } from '../features/search/Search';
import { PageHead, RobotDirectives } from '../features/head';
import { parseBoolean } from '../utils/string-utils';
import { getSearchTabs } from '../features/search/SearchUtil';
import { HotelBasketProvider, HotelDetails, HotelDetailsProvider } from '../features/hotel';
import { updateLandingPageSearch } from '../utils/recentView';
import { getWeatherAndRouteData } from '../features/content/utils/getWeatherAndRoute';
import { getQueryString, isValidEstablishmentPageQuery, useMountEffect, useTripTypeFromTrip } from '../utils';
import { LandingPageBreadcrumb } from '../features/content';
import { FunnelStepper } from '../features/stepper';
import { getSimilarHotelsDetails } from '../features/content/utils/getSimilarHotelsDetails';
export function HotelPage({ getEstabById, metaData, isLandingPage = false }) {
    var _a, _b;
    // Search
    const { asPath } = useRouter();
    const hotelLandingPage = !!asPath.includes('/2-');
    const { estabId, name, isCached } = getEstabById;
    const [initialSearchState, setSearch] = useSearch([searchCreatedRouting, setLastSearch, setRecentSearch]);
    const [searchState, setSearchState] = useState(initialSearchState);
    const tripType = useTripTypeFromTrip();
    const [isValidQuery, setIsValidQuery] = useState(true);
    const [_, setOpenField] = useCurrentOpenSearchField();
    const searchNewDates = useCallback(() => {
        setOpenField(SearchFieldEnum.DATES);
    }, [setOpenField]);
    const oc = useQueryParam('oc') || false;
    // Search unit "open" state
    const [searchUnitOpen, { open: handleOpenSearchUnit, close: handleCloseSearchUnit }] = useDisclosure(isLandingPage);
    useMountEffect(() => {
        if (parseBoolean(oc)) {
            searchNewDates();
            handleOpenSearchUnit();
        }
    });
    const handleSearchUnitOpenChange = React.useCallback((open) => {
        if (open) {
            handleOpenSearchUnit();
        }
        else {
            handleCloseSearchUnit();
        }
    }, [handleCloseSearchUnit, handleOpenSearchUnit]);
    // We have two options here, the hotel rooms data can come down from the availability query, or we could use the rooms hook below to fetch and mutate rooms data
    // This would mean the rooms component is a stand alone peice of functionalty, handling all its data fetching, mutating, local state, and global side logic with side effects
    const hotelDetails = getEstabById; // Should staticDetails become getEstabById or Hotel (thats the type). Avoiding making the change to mitigate merge conflicts. Squad D should pick this up
    const { configSettings: { HCDisableRobots, HotelHolidayDefaultAdultsPerRoom, HcSearchFunnelOptions, DefaultSearchDepartureAirport, DefaultSearchDestination, DefaultSearchDuration, ProductCacheEnabled, DefaultSearchDurationOffset, }, } = useGlobalContext();
    const isCacheEnabled = parseBoolean(ProductCacheEnabled);
    // Modify initial search state to include hotel data and lastSearch param if available
    useEffect(() => {
        if (isLandingPage) {
            const prepopulatedInput = {
                searchParam: initialSearchState,
                id: estabId,
                title: name,
                legacyType: 1,
                destination: DefaultSearchDestination,
                duration: DefaultSearchDuration,
                durationOffset: parseInt(DefaultSearchDurationOffset),
                airports: DefaultSearchDepartureAirport,
                adults: HotelHolidayDefaultAdultsPerRoom,
                isCacheEnabled,
                isCached: isCached !== null && isCached !== void 0 ? isCached : false,
                isDestinationOrEstabPage: true,
            };
            const updatedState = updateLandingPageSearch(prepopulatedInput);
            setSearchState(updatedState);
        }
    }, [
        DefaultSearchDepartureAirport,
        DefaultSearchDestination,
        DefaultSearchDuration,
        DefaultSearchDurationOffset,
        HotelHolidayDefaultAdultsPerRoom,
        estabId,
        initialSearchState,
        isCacheEnabled,
        isCached,
        isLandingPage,
        name,
    ]);
    useEffect(() => {
        setIsValidQuery(isValidEstablishmentPageQuery());
    }, []);
    const stepperLoading = isValidQuery && tripType === null;
    const stepper = useMemo(() => {
        if (isLandingPage && (hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.breadcrumbTrail)) {
            return React.createElement(LandingPageBreadcrumb, { breadcrumbs: hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.breadcrumbTrail });
        }
        if (!isLandingPage) {
            return (React.createElement(FunnelStepper, { funnelStep: FunnelStep.ChooseRoom, queryString: getQueryString(router === null || router === void 0 ? void 0 : router.query()), estabId: estabId, tripType: tripType !== null && tripType !== void 0 ? tripType : undefined, loading: stepperLoading }));
        }
        return null;
    }, [isLandingPage, hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.breadcrumbTrail, estabId, tripType, stepperLoading]);
    const { largeUp } = useBreakpoints();
    const isMobileLayout = !largeUp;
    // Search unit
    const { pathname } = useRouter();
    const trip = isLandingPage ? 'EstabLandingPage' : pathname.includes('holidays') ? TripType.Holiday : TripType.Hotel;
    const searchFunnel = getSearchTabs(trip, HcSearchFunnelOptions);
    const similarHotelsData = isLandingPage ? getSimilarHotelsDetails((_a = metaData === null || metaData === void 0 ? void 0 : metaData.components) !== null && _a !== void 0 ? _a : undefined) : undefined;
    const weatherRouteData = getWeatherAndRouteData((_b = metaData === null || metaData === void 0 ? void 0 : metaData.components) !== null && _b !== void 0 ? _b : undefined);
    return (React.createElement(React.Fragment, null,
        isLandingPage ? (React.createElement(PageHead, { pageTitle: hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.name, pageDescription: hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.overview, canonical: metaData === null || metaData === void 0 ? void 0 : metaData.canonical, linkTags: metaData === null || metaData === void 0 ? void 0 : metaData.linkTags })) : (
        // For funnel pages, we also want the hotel name to be displayed in the browser tab.
        React.createElement(PageHead, { pageTitle: hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.name })),
        parseBoolean(HCDisableRobots) && !isLandingPage && React.createElement(RobotDirectives, { values: ['nofollow', 'noindex'] }),
        React.createElement(Search, { state: isLandingPage ? searchState : initialSearchState, action: setSearch, tracking: pushSearchCriteria, collapsible: true, open: searchUnitOpen, onOpenChange: handleSearchUnitOpenChange, hotel: getEstabById, showToggle: true, searchFunnel: searchFunnel, sticky: isLandingPage, isHotelLandingPageSearchLabel: !!hotelLandingPage }),
        !isMobileLayout && React.createElement(Container, null, stepper),
        React.createElement(HotelBasketProvider, { estabId: hotelDetails.estabId },
            React.createElement(HotelDetailsProvider, null,
                React.createElement(HotelDetails, { hotelDetails: hotelDetails, pageVariant: isLandingPage ? 'landing-page' : 'funnel', similarHotelsData: similarHotelsData, weatherRouteDetails: weatherRouteData, onTracking: pushHotelDetails, onSearchUnitOpenChange: handleSearchUnitOpenChange })))));
}
